// Connection Notification - Floating notification for connection status

import React, { useState, useEffect } from 'react';

interface ConnectionNotificationProps {
  dataFreshness: {
    isUsingCache: boolean;
    oldestDataAge: number;
    hasStaleData: boolean;
    lastSuccessfulFetch: number;
    dataQuality: 'fresh' | 'cached' | 'stale' | 'missing';
  };
  isFetching: boolean;
  error: string | null;
  canRetry: boolean;
  timeUntilRetry: number;
  onRefresh: () => void;
}

const ConnectionNotification: React.FC<ConnectionNotificationProps> = ({
  dataFreshness,
  isFetching,
  error,
  canRetry,
  timeUntilRetry,
  onRefresh
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Show notification when there are issues or when using cached data
  useEffect(() => {
    const shouldShow = (
      (error && !isDismissed) ||
      (dataFreshness.dataQuality === 'stale' && !isDismissed) ||
      (dataFreshness.isUsingCache && dataFreshness.oldestDataAge > 300 && !isDismissed) // 5+ minutes old
    );

    setIsVisible(shouldShow);
  }, [error, dataFreshness, isDismissed]);

  // Auto-dismiss after successful refresh
  useEffect(() => {
    if (dataFreshness.dataQuality === 'fresh' && !error) {
      setIsDismissed(false);
    }
  }, [dataFreshness.dataQuality, error]);

  const handleDismiss = () => {
    setIsDismissed(true);
    setIsVisible(false);
  };

  // Semantic styling: error = red, degraded = amber, info = neutral.
  // Neutral card surface with a colored accent — never a saturated block.
  const getNotificationStyle = () => {
    if (error && !dataFreshness.isUsingCache) {
      return {
        accent: 'bg-red-500',
        iconColor: 'text-red-500',
        iconPath: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
        title: 'Connection Failed'
      };
    }
    if (dataFreshness.dataQuality === 'stale') {
      return {
        accent: 'bg-amber-500',
        iconColor: 'text-amber-500',
        iconPath: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
        title: 'Data is Stale'
      };
    }
    if (dataFreshness.isUsingCache) {
      return {
        accent: 'bg-amber-500',
        iconColor: 'text-amber-500',
        iconPath: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
        title: 'Using Cached Data'
      };
    }
    return {
      accent: 'bg-gray-400 dark:bg-slate-500',
      iconColor: 'text-gray-400 dark:text-slate-400',
      iconPath: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
      title: 'Updating...'
    };
  };

  const getNotificationMessage = () => {
    if (error && !dataFreshness.isUsingCache) {
      return 'Unable to connect to mining services';
    }
    if (dataFreshness.dataQuality === 'stale') {
      return 'Some data may be outdated';
    }
    if (dataFreshness.isUsingCache) {
      const ageMinutes = Math.floor(dataFreshness.oldestDataAge / 60);
      return `Showing data from ${ageMinutes}m ago`;
    }
    return 'Fetching latest data...';
  };

  const getRetryInfo = () => {
    if (timeUntilRetry > 0) {
      const seconds = Math.ceil(timeUntilRetry / 1000);
      return `Retry in ${seconds}s`;
    }
    return null;
  };

  if (!isVisible) return null;

  const style = getNotificationStyle();

  return (
    <div className="fixed top-20 short:top-14 right-4 left-4 sm:left-auto z-50 max-w-sm sm:w-auto">
      <div className="card overflow-hidden shadow-lg animate-fade-in">
        <div className="flex">
          {/* Semantic accent bar */}
          <div className={`w-1 flex-shrink-0 ${style.accent}`} aria-hidden="true"></div>

          <div className="flex-1 p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${style.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={style.iconPath} />
                </svg>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{style.title}</h4>
                  <p className="text-xs text-gray-600 dark:text-slate-300 mt-1">{getNotificationMessage()}</p>

                  {getRetryInfo() && (
                    <p className="text-xs tabular-nums text-gray-500 dark:text-slate-400 mt-1">{getRetryInfo()}</p>
                  )}

                  {error && dataFreshness.isUsingCache && (
                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                      Displaying cached data while reconnecting
                    </p>
                  )}
                </div>
              </div>

              <button
                onClick={handleDismiss}
                className="ml-2 p-1 -m-1 flex-shrink-0 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300"
                aria-label="Dismiss notification"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Action buttons */}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-slate-800">
              <div className="flex items-center space-x-2">
                {isFetching && (
                  <div className="flex items-center space-x-1.5">
                    <div className="w-3 h-3 border border-gray-300 dark:border-slate-600 border-t-gray-600 dark:border-t-slate-300 rounded-full animate-spin"></div>
                    <span className="text-xs text-gray-500 dark:text-slate-400">Updating...</span>
                  </div>
                )}
              </div>

              {!isFetching && canRetry && (
                <button
                  onClick={onRefresh}
                  className="text-xs font-medium bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-md"
                >
                  Retry Now
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectionNotification;
